import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { StationTypeEnum, Transfer, TransferServiceType } from '@lx/shared/data-access/products';
import { FormsModule } from '@angular/forms';
import { InputSwitchModule } from 'primeng/inputswitch';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { TransferTypeEnum } from '@lx/quote-tool/shared/types';
import { Observable } from 'rxjs';
import { TransferSelectorOptionsComponent } from '../transfer-selector-options/transfer-selector-options.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Amount } from '@lx/shared/data-access/types';
import { deepClone } from '@lx/shared/utils';
import { Flows, Flow } from '@lx/quote-tool/lite/shared/types';
import { GoogleAnalyticsEventEnum } from '../../google-analytics';
import { GoogleAnalyticsClickEventDirective } from '@lx/shared/google-analytics';

@Component({
  standalone: true,
  selector: 'lx-transfers-selector',
  templateUrl: './transfers-selector.component.html',
  styleUrls: ['./transfers-selector.component.scss'],
  imports: [FormsModule, InputSwitchModule, NgIf, TransferSelectorOptionsComponent, NgClass, AsyncPipe, GoogleAnalyticsClickEventDirective],
})
export class TransfersSelectorComponent implements OnInit {
  @Input() groupedTransfers: Record<TransferServiceType, Transfer[]> | undefined;
  @Input() preselectedTransferRows$: Observable<number[]> | undefined;
  @Input() activeFlow$: Observable<Flow> | undefined;

  @Output() transferRowsSelected = new EventEmitter<number[]>();

  roundTrip = true;

  transferTypes = TransferTypeEnum;

  selectedTransferRows: Record<TransferServiceType, number> | undefined;

  // Auxiliary array to store sum of prices for transfers with same code (used when round trip flag is enabled)
  roundTripTransfers: Transfer[] = [];

  sameArrivalDepartureEventName = GoogleAnalyticsEventEnum.QuoteTransferToggle;

  protected readonly stationType = StationTypeEnum;
  protected readonly Flows = Flows;

  private readonly defaultEmptyTransferRows = {
    [TransferTypeEnum.ARRIVAL]: undefined,
    [TransferTypeEnum.DEPARTURE]: undefined,
  } as Record<TransferServiceType, number>;

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.preselectedTransferRows$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((preSelectedTransferRows) => {
      // Preselect the transfer rows from store or the first transfer of each type if no transfer is selected
      if (preSelectedTransferRows?.length) {
        this.selectedTransferRows = { ...this.defaultEmptyTransferRows };
        preSelectedTransferRows.forEach((transferRow) => {
          const transfer = Object.values(this.groupedTransfers)
            .flat()
            ?.find((transfer) => transfer.row === transferRow);
          if (transfer) {
            this.selectedTransferRows[transfer.transferServiceType] = transfer.row;
          }
        });
        this.roundTrip = this.isSameArrivalDeparture();
      } else {
        this.initSelectedTransferRows();
      }
      this.fillRoundTripTransfers();
      this.transferRowsSelected.emit(this.extractListOfTransfersFromSelectedTransferRows());
    });
  }

  /**
   * If the roundTrip is true, it will select the same transfer for both arrival and departure.
   */
  preselectTransferRows() {
    if (this.roundTrip) {
      this.setSelectedTransferRow(this.selectedTransferRows.ARRIVAL || this.groupedTransfers.ARRIVAL?.[0]?.row);
    }
  }

  /**
   * Set the selected transfers.
   * If the roundTrip is true, it will select both the arrival and departure transfers.
   */
  setSelectedTransferRow(transferRow: number) {
    if (this.roundTrip) {
      const arrivalTransfer = this.groupedTransfers.ARRIVAL?.find(({ row: arrivalTransferRow }) => arrivalTransferRow === transferRow);
      this.selectedTransferRows.ARRIVAL = transferRow;
      this.selectedTransferRows.DEPARTURE = this.groupedTransfers.DEPARTURE?.find(({ code }) => code === arrivalTransfer?.code)?.row;
    } else {
      const transfer = Object.values(this.groupedTransfers)
        .flat()
        ?.find((transfer) => transfer.row === transferRow);
      if (transfer) {
        this.selectedTransferRows[transfer.transferServiceType] = transfer.row;
      }
    }

    this.transferRowsSelected.emit(this.extractListOfTransfersFromSelectedTransferRows());
  }

  removeSelectedTransferRow(transferToRemoveRow?: number) {
    if (this.roundTrip) {
      this.selectedTransferRows = { ...this.defaultEmptyTransferRows };
    } else {
      const transferToRemove = Object.values(this.groupedTransfers)
        .flat()
        ?.find((transfer) => transfer.row === transferToRemoveRow);
      if (transferToRemove) {
        this.selectedTransferRows[transferToRemove.transferServiceType] = undefined;
      }
    }
    this.transferRowsSelected.emit(this.extractListOfTransfersFromSelectedTransferRows());
  }

  /**
   * If there are no selected transfers or both of them have the same code, the roundTrip will be true.
   */
  private isSameArrivalDeparture() {
    const arrivalTransfer = Object.values(this.groupedTransfers)
      .flat()
      ?.find((transfer) => transfer.row === this.selectedTransferRows.ARRIVAL);
    const departureTransfer = Object.values(this.groupedTransfers)
      .flat()
      ?.find((transfer) => transfer.row === this.selectedTransferRows.DEPARTURE);
    return (!this.selectedTransferRows.ARRIVAL && !this.selectedTransferRows.DEPARTURE) || arrivalTransfer?.code === departureTransfer?.code;
  }

  /**
   * Initialize the selected transfers with the first transfer on arrival array and the transfer
   * with same code in departure array.
   */
  private initSelectedTransferRows() {
    const firstArrivalTransfer = this.groupedTransfers.ARRIVAL?.[0];
    this.selectedTransferRows = {
      [TransferTypeEnum.ARRIVAL]: firstArrivalTransfer?.row,
      [TransferTypeEnum.DEPARTURE]: this.groupedTransfers.DEPARTURE?.find(({ code }) => code === firstArrivalTransfer?.code)?.row,
    } as Record<TransferServiceType, number>;
  }

  private extractListOfTransfersFromSelectedTransferRows() {
    return Object.values(this.selectedTransferRows).filter((transferRow) => !!transferRow);
  }

  private fillRoundTripTransfers() {
    // Prepare a copy of arrival transfers array to sum the prices of the departure transfers with the same code
    const roundTripTransfers = Object.values(this.groupedTransfers.ARRIVAL)
      .flat()
      .reduce((acc, transfer) => {
        return acc.concat(deepClone(transfer));
      }, []);

    // Sum to arrival transfers the prices of the departure transfers with the same code
    const departureTransfers = Object.values(this.groupedTransfers.DEPARTURE).flat();
    for (const transfer of roundTripTransfers) {
      const departureTransfer = departureTransfers.find(({ code }) => code === transfer.code);
      if (departureTransfer) {
        transfer.multiCurrencyPrice = [...this.sumPrices(transfer.multiCurrencyPrice, departureTransfer.multiCurrencyPrice)];
      }
    }

    this.roundTripTransfers = roundTripTransfers;
  }

  private sumPrices(prices: Amount[], pricesToSum: Amount[]): Amount[] {
    if (!prices?.length || !pricesToSum?.length) {
      return [];
    }

    const newPrices = [...prices];

    for (const price of pricesToSum) {
      const existingPrice = newPrices.find((existingPrice) => existingPrice.currency === price.currency);
      if (existingPrice) {
        existingPrice.value += price.value;
      } else {
        newPrices.push({ ...price });
      }
    }

    return newPrices;
  }
}
